import * as THREE from 'three';
import versions from 'virtual:asset-versions';

// Loading the site's files reliably.
//  - Versioned URLs: every request for a model or data file asks for /path?v=<content hash> (vite-plugins/
//    asset-versions.ts), through fetch and through three.js's loaders (the default loading manager's URL modifier),
//    so the browser cache and the service worker can keep them for good and a deploy refreshes only what changed.
//  - Retries: a same-origin GET that fails on the network, times out at the server (408), is rate-limited (429) or
//    gets a server error (5xx) is retried with exponential backoff and jitter (0.5 s, 1 s, 2 s, 4 s) before it fails,
//    which covers the dropped connections and flaky mobile data that used to leave the studio or the city half loaded.

export function versioned(url: string) {
  try {
    const u = new URL(url, location.href);
    if (u.origin !== location.origin || u.searchParams.has('v')) return url;
    const h = versions[u.pathname];
    if (!h) return url;
    u.searchParams.set('v', h);
    return u.pathname + u.search;
  } catch { return url; }
}

const RETRY_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
let installed = false;
export const netStats = { retries: 0, failures: 0 };

export function installNetwork(tries = 5) {
  if (installed) return;
  installed = true;
  THREE.DefaultLoadingManager.setURLModifier(versioned);
  const raw = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const req = input instanceof Request ? input : null;
    const method = (init?.method ?? req?.method ?? 'GET').toUpperCase();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    let same = false;
    try { same = new URL(url, location.href).origin === location.origin; } catch { /* not a URL */ }
    if (!same || method !== 'GET') return raw(input, init);
    const target = req ? input : versioned(url);
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await raw(target, init);
        if (!RETRY_STATUS.has(res.status) || attempt >= tries - 1) return res;
      } catch (e) {
        if ((e as Error).name === 'AbortError' || init?.signal?.aborted || attempt >= tries - 1) { netStats.failures++; throw e; }
      }
      netStats.retries++;
      await wait(500 * 2 ** attempt * (0.75 + Math.random() * 0.5));
    }
  };
}
